/**
 * The environment's seeded PRNG.
 *
 * `Math.random` is banned in every generator under src/render for the same
 * reason it is banned in the camera and the particle system: a stadium that
 * builds a different crowd on every reload cannot be tested, and "the seats
 * look wrong in the corner" stops being a reproducible report.
 *
 * This is deliberately a SEPARATE copy of the mulberry32 in ./effects rather
 * than an import. The environment and the effects system are built and revised
 * independently, and a shared eight-line PRNG is not worth a coupling that
 * would make a rename in one file break the other. The algorithm is fixed by
 * its constants, so the two streams are and will stay identical for a seed.
 */

/** mulberry32. Uniform, fast, and exactly reproducible from its seed. */
export class StadiumRng {
  private state: number;

  constructor(seed = 0x9e3779b9) {
    // Seed 0 degenerates into a short cycle; fall back to the golden ratio.
    this.state = (seed >>> 0) || 0x9e3779b9;
  }

  /** Uniform in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform in [lo, hi). */
  range(lo: number, hi: number): number {
    return lo + (hi - lo) * this.next();
  }

  /** Uniform in [-1, 1). */
  signed(): number {
    return this.next() * 2 - 1;
  }

  /** Uniform integer in [0, n). Returns 0 for n <= 0. */
  int(n: number): number {
    if (!(n > 0)) return 0;
    return Math.min(n - 1, Math.floor(this.next() * n));
  }

  /** True with probability `p`. */
  chance(p: number): boolean {
    return this.next() < p;
  }
}

/** Clamp to [0, 1]. Used by every ramp in the environment builders. */
export const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t);

/** Hermite ease used by the sky ramps and the crowd shimmer. */
export const smoothstep01 = (t: number): number => {
  const k = clamp01(t);
  return k * k * (3 - 2 * k);
};

/**
 * Piecewise-linear ramp through `stops`, each `[position, ...channels]`, sorted
 * ascending by position. Pure; the sky gradient and the crowd palette are both
 * expressed as one of these so their shape can be asserted numerically.
 */
export const rampAt = (
  stops: readonly (readonly number[])[],
  t: number
): number[] => {
  if (stops.length === 0) return [];
  const channels = stops[0].length - 1;
  if (t <= stops[0][0]) return stops[0].slice(1);
  const last = stops[stops.length - 1];
  if (t >= last[0]) return last.slice(1);
  for (let i = 1; i < stops.length; i++) {
    const b = stops[i];
    if (t > b[0]) continue;
    const a = stops[i - 1];
    const span = b[0] - a[0];
    const k = span <= 0 ? 0 : (t - a[0]) / span;
    const out = new Array<number>(channels);
    for (let c = 0; c < channels; c++) out[c] = a[c + 1] + (b[c + 1] - a[c + 1]) * k;
    return out;
  }
  return last.slice(1);
};
